import React from 'react';

/**
 * `useDelayedVisibility` 用来抑制“只闪一下”的二次 loading UI。
 *
 * 典型场景是：
 * - 启动脚本骨架已经兜住首屏；
 * - React 接管后，业务页自己的 loading 很快又结束；
 * - 如果立即渲染第二层 skeleton，用户会感知到一次不必要的闪回。
 *
 * 因此这里采用：
 * - `true` 时延迟一小段时间再显示；
 * - `false` 时立即隐藏。
 *
 * 这样只有真正持续中的加载才会露出第二层 skeleton，
 * 短暂的中间态会被直接“吃掉”。
 */
const useDelayedVisibility = (visible: boolean, delayMs = 160): boolean => {
  const [delayedVisible, setDelayedVisible] = React.useState(false);

  React.useEffect(() => {
    if (!visible) {
      setDelayedVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setDelayedVisible(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [delayMs, visible]);

  return delayedVisible;
};

export default useDelayedVisibility;
